import { useMutation } from '@northladder/services';
import {
  ErrorEventsEnum,
  errorLogToRemoteUtil,
  IApiError,
} from '@northladder/utilities';

import { ILoginUserData } from '../types';
import { USER_ACCOUNTS_API } from '../userAccountsAPIClient';

/**
 * This is to track the verify otp mutation keys in react query cache.
 */
const MUT_VERIFY_EMAIL_OTP_KEY = 'verify-email-otp-mutation-key';

interface IVerifyOtpOptions {
  onErrorCallback?: (err: IApiError) => void;
  /**
   * Callback to be by the calling app if the login is successful.
   * Note: Usually this would be best handled within the onSuccess of the hook
   * itself but since the login would require additional platform specific
   * features like `next/router` & `localStorage` on the web and
   * `react-navigation` & `asyncStorage` on mobile, this will be done in the
   * callback of the respective platform without having to embed them in the
   * util.
   */
  onSuccessCallback: (loginData: ILoginUserData) => void;
}

/**
 * This validates user OTP request and retrieves the auth user info and token.
 * Provides data fetching states and a callback to be used in the redirection
 * later.
 */
export const useVerifyEmailOtpMutation = ({
  onErrorCallback,
  onSuccessCallback,
}: IVerifyOtpOptions) => {
  const { mutate, isPending, error, data } = useMutation({
    mutationKey: [MUT_VERIFY_EMAIL_OTP_KEY],
    networkMode: 'always', // Even make calls when offline
    retry: false, // For login Request, do not retry failed requests.
    mutationFn: USER_ACCOUNTS_API.validateUserEmailLoginOTP,
    onSuccess: onSuccessCallback,
    onError: (err: IApiError) => {
      errorLogToRemoteUtil({
        error,
        errorCode: ErrorEventsEnum.ERROR_IN_API_CALL,
        errorTitle: 'useVerifyPhoneNumberOtpMutation Error',
        message: error?.message,
      });

      onErrorCallback?.(err);

      return err;
    },
  });

  return {
    data,
    error,
    isPending,
    verifyOtp: mutate,
  };
};
